import { useSelector } from 'react-redux';

import { Trans, msg, t } from '@lingui/macro';
import PropTypes from 'prop-types';
import { Divider, Icon } from 'semantic-ui-react';
import styled, { css } from 'styled-components';

import { ontologyDisplayNameFromNameSelectorFactory } from 'selectors/campaign';

import ColoredDot from 'components/ui/ColoredDot';
import { LightHeader, SmallHeader } from 'components/ui/Header';
import StyledSegment from 'components/ui/Segment';
import {
  NegativePoint as NegativePointIcon,
  PositivePoint as PositivePointIcon,
} from 'components/ui/icon/SentimentIcon';
import SentimentIconSvg from 'components/ui/icon/SentimentIconSvg';
import { I18nDropdown, StyledDropdown } from 'components/ui/inputs/Dropdown';

import commonPropTypes from 'utils/commonPropTypes';
import capitalizedTranslation from 'utils/i18n';

import * as svars from 'assets/style/variables';

import ClassificationValidationForm from './ClassificationValidationForm';

function SentimentItem({ sentiment, textId, style }) {
  return (
    <span style={{ flexGrow: 1, ...style }}>
      <ColoredDot
        sentiment={sentiment}
        style={{ marginRight: svars.spaceNormalLarge }}
      />
      <Trans render={capitalizedTranslation} id={textId} />
    </span>
  );
}

SentimentItem.propTypes = {
  sentiment: PropTypes.number.isRequired,
  textId: commonPropTypes.i18nText.isRequired,
  style: commonPropTypes.style,
};

SentimentItem.defaultProps = {
  style: {},
};

const SENTIMENT_POLARITY_OPTIONS = [
  {
    key: 'very-positive',
    value: 0.8,
    text: msg({ id: 'very-positive' }),
    content: '😁',
  },
  { key: 'positive', value: 0.45, text: msg({ id: 'positive' }) },
  { key: 'neutral', value: 0.0001, text: msg({ id: 'neutral' }) },
  { key: 'negative', value: -0.45, text: msg({ id: 'negative' }) },
  { key: 'very-negative', value: -0.8, text: msg({ id: 'very-negative' }) },
].map((item) => ({
  ...item,
  content: (
    <SentimentItem
      sentiment={item.value}
      textId={item.text}
      style={{ margin: `0 ${svars.spaceSmaller}` }}
    />
  ),
}));

const findClosestSentimentPolarity = (value) => {
  if (value === null) {
    return null;
  }
  const closest = SENTIMENT_POLARITY_OPTIONS.reduce((acc, cur) =>
    Math.abs(cur.value - value) < Math.abs(acc.value - value) ? cur : acc
  );
  return closest.value;
};

const dropDownStyles = css`
  border-radius: ${svars.ctaBorderRadius};
  padding: ${svars.spaceNormal} ${svars.spaceSmaller} ${svars.spaceNormal}
    ${svars.spaceMedium} !important;
  justify-content: center;
  ${svars.hoverClickableCss}
  ${svars.activeClickableCss}
`;

const ConceptDropdown = styled(StyledDropdown)`
  &&&& {
    border: ${svars.lightBorderStyle};
    border-radius: ${svars.ctaBorderRadius};
    background: ${svars.colorWhite};
    margin-left: ${svars.spaceMedium};
    max-width: ${svars.smallInputMaxWidth};
    & .dropdown {
      ${dropDownStyles}
    }

    & .menu {
      transform: translateX(-15%);
      min-width: 320px !important;
      white-space: normal;

      & .item > .text {
        white-space: normal;
        line-height: 1.2;
      }
    }
  }
`;

const PolarityDropdown = styled(I18nDropdown)`
  &&&& {
    border: ${svars.lightBorderStyle};
    border-radius: ${svars.ctaBorderRadius};
    margin-left: ${svars.spaceMedium};
    & .dropdown {
      ${dropDownStyles}
    }
  }
`;

const classificationIcons = {
  items_texts: null,
  items_positive: PositivePointIcon,
  items_negative: NegativePointIcon,
};

function ReviewValidationElement({
  id,
  text,
  index,
  sentiment,
  classifications,
  classificationsField,
  conceptOptions,
  onAddClassification,
  onUpdateSentiment,
  onDeleteClassification,
  style,
}) {
  const ontologyNameFormatter = useSelector(
    ontologyDisplayNameFromNameSelectorFactory
  );
  const sentimentPolarity = findClosestSentimentPolarity(sentiment);
  const polarityItem = SENTIMENT_POLARITY_OPTIONS.find(
    (item) => item.value === sentimentPolarity
  );
  const chunkDisplayIndex = index + 1;

  const nOntologies = classifications ? Object.keys(classifications).length : 0;

  const headerText =
    (classificationsField === 'items_texts' &&
      t`review-chunk-${chunkDisplayIndex}`) ||
    (classificationsField === 'items_positive' &&
      t`positive-point-${chunkDisplayIndex}`) ||
    (classificationsField === 'items_negative' &&
      t`negative-point-${chunkDisplayIndex}`) ||
    null;
  const PolarityIcon = classificationIcons[classificationsField];
  return (
    <StyledSegment
      style={{
        margin: `${svars.spaceNormal} ${svars.spaceNormalLarge}`,
        ...style,
      }}
      id={id}
    >
      <SmallHeader style={{ display: 'flex' }}>
        {PolarityIcon ? <PolarityIcon /> : null}
        <Trans render={capitalizedTranslation} id={headerText} />
      </SmallHeader>
      <p style={{ margin: `${svars.spaceMedium}` }}>{text}</p>
      <Divider />
      <LightHeader
        style={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Icon name="fork" style={{ marginRight: svars.spaceNormal }} />
        <Trans render={capitalizedTranslation} id="categories" />
        <ConceptDropdown
          floating
          fluid
          scrolling
          labeled
          icon="plus"
          search
          text={t({ id: 'add-concept' })}
          options={conceptOptions}
          value=""
          onChange={onAddClassification}
          selectOnNavigation={false}
          upward={index > 0}
        />
      </LightHeader>
      <div style={{ margin: `${svars.spaceNormalLarge} 0` }}>
        {classifications
          ? Object.entries(classifications).map(
              ([ontologyName, ontologyClassifications]) => (
                <div
                  key={ontologyName}
                  style={{ marginBottom: svars.spaceMedium }}
                >
                  {nOntologies ? (
                    <SmallHeader>
                      {ontologyNameFormatter(ontologyName)}
                    </SmallHeader>
                  ) : null}
                  <ClassificationValidationForm
                    style={{ margin: svars.spaceNormalLarge }}
                    classifications={
                      ontologyClassifications?.[classificationsField]?.[index]
                    }
                    onDeleteClassification={onDeleteClassification(
                      ontologyName
                    )}
                  />
                </div>
              )
            )
          : null}
      </div>
      <Divider />
      <div>
        <LightHeader
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <SentimentIconSvg
            iconSize={svars.fontSizeMedium}
            style={{ marginRight: svars.spaceNormal }}
          />
          <Trans render={capitalizedTranslation} id="polarity" />
          <PolarityDropdown
            icon="caret down"
            value={sentimentPolarity}
            options={SENTIMENT_POLARITY_OPTIONS}
            placeholder={t({ id: 'select-a-category' })}
            onChange={onUpdateSentiment}
            trigger={
              <SentimentItem
                sentiment={polarityItem?.value}
                textId={polarityItem?.text}
              />
            }
          />
        </LightHeader>
      </div>
    </StyledSegment>
  );
}

ReviewValidationElement.propTypes = {
  id: PropTypes.string,
  text: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  onAddClassification: PropTypes.func.isRequired,
  onUpdateSentiment: PropTypes.func.isRequired,
  onDeleteClassification: PropTypes.func.isRequired,
  sentiment: PropTypes.number.isRequired,
  classificationsField: PropTypes.string.isRequired,
  conceptOptions: commonPropTypes.items.isRequired,
  classifications: commonPropTypes.classifications.isRequired,
  style: commonPropTypes.style,
};

ReviewValidationElement.defaultProps = {
  id: null,
  style: {},
};

export default ReviewValidationElement;
